import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth, useAuthDispatch } from './AuthContext';
import { SET_AUTH_USER, USER_LOGOUT } from './actionTypes';
import {
  isSessionRecoveryInProgress,
  recoverExpiredSession,
  signinSilentWithTimeout,
  startInteractiveLogin,
} from '@/util/sessionRecovery';
import type { AuthState } from '@/types';

const ApiContext = createContext<AxiosInstance | null>(null);

interface ApiProviderProps {
  children: ReactNode;
}

function buildApi(auth: AuthState, auth_dispatch: React.Dispatch<any>): AxiosInstance {
  const api = axios.create({
    baseURL: `${auth.config?.api_endpoint}/v1`,
  });

  // Request interceptor to check token expiration before making requests
  api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (config.url !== '/config/auth') {
      // Check if token is expired and refresh if needed
      const token = localStorage.getItem('accqsure_jwt');
      if (token && auth.user_manager && auth.user) {
        try {
          const user = await auth.user_manager.getUser();
          if (user && user.expires_at && user.expires_at < Date.now() / 1000) {
            console.log('Token expired, attempting refresh...');
            const refreshedUser = await recoverExpiredSession(
              auth.user_manager,
            );
            if (refreshedUser && refreshedUser.access_token) {
              localStorage.setItem('accqsure_jwt', refreshedUser.access_token);
              auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });
              config.headers['Authorization'] =
                `Bearer ${refreshedUser.access_token}`;
              console.log('Token refreshed successfully');
            } else {
              console.log('Silent refresh failed, redirecting to login');
              auth_dispatch({ type: USER_LOGOUT });
              return Promise.reject(new Error('Token refresh failed'));
            }
          } else if (user && user.access_token) {
            // Token is valid, add it to request headers
            config.headers['Authorization'] = `Bearer ${user.access_token}`;
          } else if (token) {
            // Fallback: use the token from localStorage if user object doesn't have it
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (error) {
          console.log('Error checking/refreshing token:', error);
          auth_dispatch({ type: USER_LOGOUT });
          await startInteractiveLogin(auth.user_manager);
          return Promise.reject(error);
        }
      } else if (token) {
        // No user_manager available but token exists, use it
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  });

  // Response interceptor to handle 401 responses and attempt token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        auth.user_manager &&
        auth.user
      ) {
        originalRequest._retry = true;

        try {
          console.log('401 response, attempting token refresh...');
          const refreshedUser = await recoverExpiredSession(auth.user_manager);

          if (refreshedUser && refreshedUser.access_token) {
            localStorage.setItem('accqsure_jwt', refreshedUser.access_token);
            auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });

            originalRequest.headers['Authorization'] =
              `Bearer ${refreshedUser.access_token}`;
            console.log('Token refreshed and retrying request');
            return api(originalRequest);
          } else {
            console.log('Token refresh failed, redirecting to login');
            auth_dispatch({ type: USER_LOGOUT });
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.log('Token refresh failed:', refreshError);
          auth_dispatch({ type: USER_LOGOUT });
          await startInteractiveLogin(auth.user_manager);
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return api;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const auth = useAuth();
  const auth_dispatch = useAuthDispatch();
  const [api, setApi] = useState<AxiosInstance | null>(null);

  useEffect(() => {
    if (auth.config?.api_endpoint && auth.user_manager) {
      setApi(() => {
        // must return this inside a function otherwise it tries to execute it as a function
        return buildApi(auth, auth_dispatch);
      });
    }
  }, [auth, auth_dispatch]);

  // Proactive token refresh - check token expiration every 5 minutes
  useEffect(() => {
    if (!api || !auth.user_manager || !auth.user) return;

    const userManager = auth.user_manager; // Capture to avoid null check issues in async

    const checkAndRefreshToken = async () => {
      try {
        const user = await userManager.getUser();
        if (user && user.expires_at) {
          const timeUntilExpiry = user.expires_at - Date.now() / 1000;
          // If token expires in less than 10 minutes, refresh it
          if (timeUntilExpiry < 600 && !isSessionRecoveryInProgress()) {
            console.log(
              `Token expiring in ${Math.round(timeUntilExpiry)} seconds, proactively refreshing...`,
            );
            try {
              const refreshedUser = await signinSilentWithTimeout(userManager);
              if (refreshedUser && refreshedUser.access_token) {
                localStorage.setItem(
                  'accqsure_jwt',
                  refreshedUser.access_token,
                );
                auth_dispatch({ type: SET_AUTH_USER, payload: refreshedUser });
                console.log('Token refreshed proactively');
              } else {
                console.log('Proactive token refresh returned no user');
              }
            } catch (error) {
              console.log('Proactive token refresh failed:', error);
              // Don't redirect to login here as the user might still be active
              // The request interceptor will handle this when making actual API calls
            }
          }
        }
      } catch (error) {
        console.log('Error checking token expiration:', error);
        // Don't redirect to login here as the user might still be active
        // The request interceptor will handle this when making actual API calls
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Then check every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [api, auth.user_manager, auth.user, auth_dispatch]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): AxiosInstance | null {
  return useContext(ApiContext);
}
